"use client";

import { cn } from "@/lib/utils";
import type { SquashTemplateData } from "../squash-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type SquashScorecardProps = {
  data: SquashTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function SquashScorecard({
  data,
  participants,
  variant = "feed",
}: SquashScorecardProps) {
  if (!data) return null;

  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const yourTeam = participants.filter(
    (p) => data.positions?.[p.id] === "your-team",
  );
  const opponentTeam = participants.filter(
    (p) => data.positions?.[p.id] === "opponent-team",
  );

  const yourWon = data.matchScore.yourGames > data.matchScore.opponentGames;
  const opponentWon = data.matchScore.opponentGames > data.matchScore.yourGames;

  return (
    <ScorecardShell variant={variant}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-text-primary">
            {yourTeam[0]?.displayName?.split(" ")[0] || "Team 1"}
          </span>
          {yourWon && (
            <span className="text-xs font-medium text-brand">Winner</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-2xl font-bold",
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {data.matchScore.yourGames}
          </span>
          <span className="text-text-secondary">–</span>
          <span
            className={cn(
              "text-2xl font-bold",
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {data.matchScore.opponentGames}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm font-semibold text-text-primary">
            {opponentTeam[0]?.displayName?.split(" ")[0] || "Team 2"}
          </span>
          {opponentWon && (
            <span className="text-xs font-medium text-error">Winner</span>
          )}
        </div>
      </div>

      {data.games && data.games.length > 0 && (
        <div className="mt-2 flex flex-wrap justify-center gap-2 border-t border-border pt-2">
          {data.games.map((game, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: game scores are positional and never reorder
              key={`game-${i}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {game.yourScore}-{game.opponentScore}
            </span>
          ))}
        </div>
      )}
    </ScorecardShell>
  );
}
